import {Box, GlobalStyles, Typography} from "@mui/material";
import {c, mono, cardSx} from "../landingTokens";
import {AgentBuilder} from "./AgentBuilder";
import {LiveCallDemo} from "./LiveCallDemo";
import {LiquidEther} from "../../../components/voice-orb";

// Hero CTA cards: solid fill + soft shadow only (no hairline border) so the two read as
// smooth, raised panels rather than outlined boxes. Static — no hover motion.
const HERO_CARD_SHADOW = "0 34px 80px -34px rgba(66,46,170,0.34), 0 2px 8px rgba(20,21,26,0.05)";
const heroCardSx = {
  ...cardSx,
  bgcolor: c.white,
  border: "none",
  boxShadow: HERO_CARD_SHADOW,
  transition: "none",
  "&:hover": {boxShadow: HERO_CARD_SHADOW},
};

// A single quiet mono label at the top of each hero card — no numbering, no timing.
const StepLabel = ({title}) => (
  <Box component="span" sx={{
    width: "100%", fontFamily: mono, fontSize: 10, letterSpacing: "0.2em",
    color: c.inkMuted, textTransform: "uppercase"
  }}>
    {title}
  </Box>
);

// A single quiet caption line under each card — sentence-style, not robotic tag chips.
const CardCaption = ({children, align = "center"}) => (
  <Typography sx={{width: "100%", textAlign: align, fontSize: 13, color: c.inkMuted, mt: "auto"}}>
    {children}
  </Typography>
);

export const Hero = () => (
  <Box component="section" id="live" data-screen-label="Hero" sx={{
    position: "relative",
    px: 2.75,
    pt: {xs: 5, md: 7},
    pb: {xs: 4, md: 7},
    textAlign: "center",
    overflow: "hidden"
  }}>
    <GlobalStyles
      styles={{
        "@keyframes bfDrift": {
          "0%,100%": {transform: "translate3d(0,0,0) scale(1)"},
          "50%": {transform: "translate3d(3%,-4%,0) scale(1.08)"},
        },
        "@keyframes bfFloat": {
          "0%,100%": {transform: "translateY(0)"},
          "50%": {transform: "translateY(-6px)"},
        },
        // Animated sheen that slides across the gradient numbers in the headline.
        "@keyframes headlineSheen": {
          "0%,100%": {backgroundPosition: "0% 50%"},
          "50%": {backgroundPosition: "100% 50%"},
        },
        // Concentric "ringing" pulses that radiate out from the live-call orb.
        "@keyframes orbRing": {
          "0%": {transform: "scale(1)", opacity: 0.5},
          "100%": {transform: "scale(1.65)", opacity: 0},
        },
        // Soft breathing halo behind the orb.
        "@keyframes orbHalo": {
          "0%,100%": {transform: "scale(1)", opacity: 0.55},
          "50%": {transform: "scale(1.08)", opacity: 0.85},
        },
        // Pulsing "live" status dot.
        "@keyframes livePulse": {
          "0%": {boxShadow: "0 0 0 0 rgba(16,185,129,0.5)"},
          "70%": {boxShadow: "0 0 0 10px rgba(16,185,129,0)"},
          "100%": {boxShadow: "0 0 0 0 rgba(16,185,129,0)"},
        },
        "@media (prefers-reduced-motion: no-preference)": {
          ".openbrain-landing .hero-enter": {animation: "rise .9s cubic-bezier(.16,1,.3,1) both"},
          ".openbrain-landing .hero-orb-ring": {animation: "orbRing 3.2s cubic-bezier(.16,1,.3,1) infinite"},
          ".openbrain-landing .hero-orb-halo": {animation: "orbHalo 7s ease-in-out infinite"},
          ".openbrain-landing .live-dot": {animation: "livePulse 1.8s ease-in-out infinite"},
          ".openbrain-landing .hero-num": {
            backgroundSize: "220% auto",
            animation: "headlineSheen 7s ease-in-out infinite",
          },
        },
      }}
    />

    {/* Mouse-reactive fluid backdrop (React Bits LiquidEther). Transparent at rest — soft
        purple/pink trails bloom only where the cursor moves, then fade. pointerEvents:none lets
        clicks pass through to the cards; LiquidEther still tracks the cursor via window listeners,
        so it reacts whenever the pointer is anywhere over the hero. */}
    <Box aria-hidden sx={{position: "absolute", inset: 0, zIndex: 0, opacity: 0.3, pointerEvents: "none"}}>
      <LiquidEther
        colors={["#8B7CFF", "#E2A6FF", "#C7D2FE"]}
        mouseForce={8}
        cursorSize={120}
        resolution={0.5}
        autoDemo
        autoSpeed={0.25}
        autoIntensity={1.0}
        autoResumeDelay={800}
        autoRampDuration={1.5}
      />
    </Box>

    {/* Ambient bloom behind the headline. */}
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        top: -320,
        left: "50%",
        transform: "translateX(-50%)",
        width: 1400,
        height: 900,
        background:
          "radial-gradient(ellipse at 50% 40%, rgba(139,124,255,0.42), rgba(220,235,250,0.65) 45%, rgba(248,249,252,0) 70%)",
        filter: "blur(10px)",
        pointerEvents: "none",
        animation: "glowpulse 9s ease-in-out infinite",
      }}
    />

    <Box sx={{position: "relative", zIndex: 1, maxWidth: 1000, mx: "auto"}}>
      <Typography sx={{
        fontFamily: mono,
        fontSize: "11.5px",
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: c.purpleDeep,
        mb: 2
      }}>
        OpenBrain · AI Voice Agent
      </Typography>

      <Typography
        component="h1"
        sx={{
          fontSize: "clamp(36px, 5.6vw, 74px)",
          lineHeight: 0.98,
          letterSpacing: "-0.045em",
          fontWeight: 700,
          maxWidth: 900,
          mx: "auto",
          textWrap: "balance",
          textShadow: "0 12px 38px rgba(94,88,190,0.08)"
        }}
      >
        Cut contact center workload by{" "}
        <Box component="span"
             sx={{display: "inline-flex", alignItems: "baseline", gap: {xs: 0.8, md: 1.5}, whiteSpace: "nowrap"}}>
          <Box component="span" className="hero-num" sx={{
            background: `linear-gradient(100deg, ${c.purple}, ${c.purpleDeep}, ${c.purple})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text"
          }}>50</Box>
          <Box component="span"
               sx={{fontSize: "0.58em", color: c.inkMuted, fontWeight: 500, letterSpacing: "-0.02em"}}>–</Box>
          <Box component="span" className="hero-num" sx={{
            background: `linear-gradient(100deg, ${c.purpleBrand}, ${c.purpleDeep}, ${c.purpleBrand})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text"
          }}>70%</Box>
        </Box>
        .
      </Typography>
    </Box>

    {/* ─── Supporting paragraph ────────────────────────────────────────────── */}
    <Typography sx={{
      position: "relative", zIndex: 1,
      maxWidth: 620, mx: "auto", mt: {xs: 2.5, md: 3},
      fontSize: {xs: 15.5, md: 17}, lineHeight: 1.55, color: c.inkSoft, textWrap: "pretty"
    }}>
      Inbound calls answered, understood, and resolved instantly — with a clean handoff
      to your team the moment it matters.
    </Typography>

    {/* ─── DUAL CTA ROW: talk to it · OR · build it ────────────────────────── */}
    <Box className="hero-enter" sx={{
      position: "relative", zIndex: 1, display: "flex", flexWrap: "wrap",
      justifyContent: "center", alignItems: "stretch",
      gap: 2.25, maxWidth: 1140, mx: "auto", mt: {xs: 3.5, md: 4.25}
    }}>
      {/* LEFT CARD — talk to it */}
      <Box sx={{...heroCardSx, flex: "1 1 380px", minWidth: 300, display: "flex",
                flexDirection: "column", alignItems: "center", gap: 3, textAlign: "center"}}>
        <StepLabel title="Hear it live" />
        <Box sx={{flex: 1, width: "100%", display: "flex", alignItems: "center", justifyContent: "center"}}>
          <LiveCallDemo hero size={212} />
        </Box>
        <CardCaption>Works with your Avaya. GDPR-compliant.</CardCaption>
      </Box>

      {/* OR token */}
      <Box aria-hidden sx={{
        flex: "0 0 auto", alignSelf: "center",
        width: 52, height: 52, borderRadius: "50%", display: {xs: "none", md: "grid"}, placeItems: "center",
        bgcolor: c.white, boxShadow: "0 12px 30px -12px rgba(66,46,170,0.45)",
        fontFamily: mono, fontSize: 10.5, letterSpacing: "0.14em", color: c.purpleInk
      }}>OR</Box>

      {/* RIGHT CARD — build it */}
      <Box sx={{...heroCardSx, flex: "1 1 380px", minWidth: 300, display: "flex",
                flexDirection: "column", gap: 2.25, textAlign: "left"}}>
        <StepLabel title="Build your own" />
        <AgentBuilder embedded />
        <CardCaption align="left">Many languages, live in one click.</CardCaption>
      </Box>
    </Box>
  </Box>
);
